import { createElement, Fragment, type ReactNode } from "react";

export type IconName =
  | "grid"
  | "companies"
  | "map"
  | "queue"
  | "doc"
  | "send"
  | "history"
  | "sync"
  | "shield"
  | "bell"
  | "search"
  | "check"
  | "x"
  | "alert"
  | "info"
  | "clock"
  | "out"
  | "plug"
  | "file"
  | "qr"
  | "down"
  | "chart"
  | "lock"
  | "filter"
  | "refresh"
  | "play"
  | "users"
  | "inbox"
  | "plus"
  | "mail"
  | "key"
  | "settings";

type El = [string, Record<string, string>];

const path = (d: string): El => ["path", { d }];
const circle = (cx: string, cy: string, r: string): El => ["circle", { cx, cy, r }];
const rect = (x: string, y: string, width: string, height: string, rx?: string): El => [
  "rect",
  rx ? { x, y, width, height, rx } : { x, y, width, height },
];

/** Ported verbatim from the mock's ICO map, with the nav key renamed to `companies`. */
const ICO_PATHS: Record<IconName, El[]> = {
  grid: [path("M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z")],
  companies: [path("M3 21V7l6-4v6l6-4v6l6-4v14z"), path("M9 21v-4h6v4")],
  map: [
    path("M4 7h6M14 17h6"),
    path("M10 7a4 4 0 0 0 4 4h2a4 4 0 0 1 4 4v2"),
    circle("4", "7", "2"),
    circle("20", "17", "2"),
  ],
  queue: [path("M3 6h18M3 12h18M3 18h11")],
  doc: [path("M14 3v5h5"), path("M14 3H6v18h12V8z"), path("M9 13h6M9 17h4")],
  send: [path("M22 2 11 13"), path("M22 2 15 22l-4-9-9-4z")],
  history: [path("M3 12a9 9 0 1 0 3-6.7L3 8"), path("M3 3v5h5"), path("M12 7v5l3 2")],
  sync: [
    path("M21 12a9 9 0 0 1-15.5 6.2L3 16"),
    path("M3 12a9 9 0 0 1 15.5-6.2L21 8"),
    path("M21 3v5h-5M3 21v-5h5"),
  ],
  shield: [path("M12 3l8 3v6c0 5-3.4 8.4-8 9-4.6-.6-8-4-8-9V6z"), path("m9 12 2 2 4-4")],
  bell: [path("M18 8a6 6 0 1 0-12 0c0 7-3 8-3 8h18s-3-1-3-8"), path("M13.7 21a2 2 0 0 1-3.4 0")],
  search: [circle("11", "11", "7"), path("m21 21-4.3-4.3")],
  check: [path("m4 12 5 5L20 6")],
  x: [path("M18 6 6 18M6 6l12 12")],
  alert: [
    path("M12 9v4M12 17h.01"),
    path("M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"),
  ],
  info: [circle("12", "12", "9"), path("M12 16v-4M12 8h.01")],
  clock: [circle("12", "12", "9"), path("M12 7v5l3 2")],
  out: [path("M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"), path("m16 17 5-5-5-5M21 12H9")],
  plug: [path("M9 2v6M15 2v6"), path("M6 8h12v3a6 6 0 0 1-12 0z"), path("M12 17v5")],
  file: [path("M13 2v7h7"), path("M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z")],
  qr: [
    path("M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3z"),
    path("M14 14h3v3h-3zM19 14h2v2M14 19h3v2M19 19h2v2"),
  ],
  down: [path("M12 4v14M6 13l6 6 6-6")],
  chart: [path("M3 3v18h18"), path("M7 15l4-5 3 3 5-7")],
  lock: [rect("4", "10", "16", "11", "2"), path("M8 10V7a4 4 0 0 1 8 0v3")],
  filter: [path("M3 4h18l-7 8v7l-4 2v-9z")],
  refresh: [path("M21 12a9 9 0 1 1-2.6-6.4L21 8"), path("M21 3v5h-5")],
  play: [path("M6 4l14 8-14 8z")],
  users: [
    path("M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"),
    circle("9", "7", "4"),
    path("M22 21v-2a4 4 0 0 0-3-3.9"),
  ],
  inbox: [
    path("M22 12h-6l-2 3h-4l-2-3H2"),
    path("M5.5 5.1 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.5-6.9A2 2 0 0 0 16.7 4H7.3a2 2 0 0 0-1.8 1.1z"),
  ],
  plus: [path("M12 5v14M5 12h14")],
  mail: [rect("2", "4", "20", "16", "2"), path("m2 7 10 6 10-6")],
  key: [circle("7.5", "15.5", "4.5"), path("m10.7 12.3 8.3-8.3 3 3-2 2-2-2M17 8l2 2")],
  settings: [
    circle("12", "12", "3"),
    path(
      "M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34" +
        " 1.7 1.7 0 0 0-1 1.55V21a2 2 0 0 1-4 0v-.09A1.7 1.7 0 0 0 9 19.36a1.7 1.7 0 0 0-1.87.34" +
        "l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.64 15a1.7 1.7 0 0 0-1.55-1H3a2 2 0" +
        " 0 1 0-4h.09A1.7 1.7 0 0 0 4.64 9a1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06" +
        ".06A1.7 1.7 0 0 0 9 4.64a1.7 1.7 0 0 0 1-1.55V3a2 2 0 0 1 4 0v.09a1.7 1.7 0 0 0 1 1.55" +
        " 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.36 9" +
        "a1.7 1.7 0 0 0 1.55 1H21a2 2 0 0 1 0 4h-.09a1.7 1.7 0 0 0-1.55 1z",
    ),
  ],
};

export const ICO: Record<IconName, ReactNode> = Object.fromEntries(
  Object.entries(ICO_PATHS).map(([name, els]) => [
    name,
    createElement(
      Fragment,
      null,
      ...els.map(([tag, props], i) => createElement(tag, { key: i, ...props })),
    ),
  ]),
) as Record<IconName, ReactNode>;
